/**
 * FeatureBus — pure extractors for Track A sonification.
 * Signal honesty: total speed + param/geometry proxies (no |u|/|g| buffers).
 */

import { functionalGraphCycles, chainDepths } from "../analysis/graph";
import {
  meanLinkDistances,
  radiusOfGyration,
} from "../analysis/metrics";
import type { SimView } from "../core/simView";
import type { GravityMode, SimParams } from "../sim/params";
import { strideForN } from "./defaults";
import type { AudioParams } from "./params";

export type AudioFeatureFrame = {
  dtSec: number;
  n: number;
  dim: number;
  /** Effective stride used (≥1 after resolving auto). */
  stride: number;

  kinematics: {
    meanSpeed: number;
    p90Speed: number;
    rmsSpeed: number;
    meanTurn: number;
    activeFrac: number;
  };

  gravity: {
    modeActive: boolean;
    mode: GravityMode;
    strengthNorm: number;
    meanWellDist: number;
    clump: number;
  };

  topology: {
    epoch: number;
    epochChanged: boolean;
    freeze: boolean;
    rechooseProb: number;
    nCycles: number;
    meanChainDepth: number;
    meanFriendDist: number;
    meanEnemyDist: number;
    /**
     * High when friends are close relative to enemies.
     * enemy / (friend + enemy + ε)
     */
    linkTension: number;
  };

  social?: {
    meanSocialStep: number;
  };
};

export type FeatureBusPrev = {
  epoch: number;
  headings?: Float64Array;
  graph?: { nCycles: number; meanChainDepth: number; frameStamp: number };
  link?: { meanFriendDist: number; meanEnemyDist: number; frameStamp: number };
};

const SPEED_EPS = 1e-6;
const RG_REF = 0.85;

export function linkTensionFromDists(
  meanFriendDist: number,
  meanEnemyDist: number,
): number {
  return meanEnemyDist / (meanFriendDist + meanEnemyDist + 1e-6);
}

function wrapPi(d: number): number {
  let x = d;
  while (x > Math.PI) x -= Math.PI * 2;
  while (x < -Math.PI) x += Math.PI * 2;
  return x;
}

function percentile90(sortedAsc: number[]): number {
  if (sortedAsc.length === 0) return 0;
  const idx = Math.min(
    sortedAsc.length - 1,
    Math.floor(0.9 * (sortedAsc.length - 1)),
  );
  return sortedAsc[idx]!;
}

export function extractAudioFeatures(
  view: SimView,
  sim: SimParams,
  audio: AudioParams,
  prev: FeatureBusPrev,
  opts?: { dtSec?: number; speedsScratch?: Float64Array; frameStamp?: number },
): { frame: AudioFeatureFrame; next: FeatureBusPrev } {
  const n = view.n;
  const dim = view.dim;
  const dtSec = opts?.dtSec ?? 0;
  const frameStamp = opts?.frameStamp ?? 0;
  const strideRaw = audio.particleStride === 0 ? strideForN(n) : audio.particleStride;
  const stride = Math.max(1, Math.floor(strideRaw));

  const speeds =
    opts?.speedsScratch && opts.speedsScratch.length >= n
      ? opts.speedsScratch
      : new Float64Array(n);
  view.writeSpeeds(speeds);

  // —— Kinematics (strided sample) ——
  const sample: number[] = [];
  let sum = 0;
  let sum2 = 0;
  let active = 0;
  let turnSum = 0;
  let turnCount = 0;
  const headings = new Float64Array(Math.ceil(n / stride) || 1);
  let hi = 0;
  const prevHead = prev.headings;

  for (let i = 0, s = 0; i < n; i += stride, s++) {
    const sp = speeds[i] ?? 0;
    sample.push(sp);
    sum += sp;
    sum2 += sp * sp;
    if (sp > SPEED_EPS) active++;

    if (dim >= 2) {
      const b = i * dim;
      const vx = view.velocities[b] ?? 0;
      const vy = view.velocities[b + 1] ?? 0;
      const th = Math.atan2(vy, vx);
      headings[hi] = th;
      if (prevHead && s < prevHead.length) {
        turnSum += Math.abs(wrapPi(th - prevHead[s]!));
        turnCount++;
      }
      hi++;
    }
  }

  const sc = sample.length || 1;
  const meanSpeed = sum / sc;
  const rmsSpeed = Math.sqrt(sum2 / sc);
  const sorted = sample.slice().sort((a, b) => a - b);
  const p90Speed = percentile90(sorted);
  const activeFrac = active / sc;
  const meanTurn = turnCount > 0 ? turnSum / turnCount : 0;

  // —— Gravity proxies ——
  const mode = sim.gravityMode ?? "none";
  const modeActive = mode !== "none";
  const strengthNorm = Math.max(
    0,
    Math.min(1, (sim.gravityStrength ?? 0) / 0.01),
  );
  const Rg = radiusOfGyration(view.positions, dim, n);
  const clump = 1 - Math.max(0, Math.min(1, Rg / RG_REF));

  let meanWellDist = 0;
  if (mode === "origin" || mode === "com" || mode === "type_com") {
    const com = new Float64Array(dim);
    if (mode !== "origin") {
      for (let i = 0; i < n; i++) {
        const b = i * dim;
        for (let d = 0; d < dim; d++) com[d]! += view.positions[b + d]!;
      }
      for (let d = 0; d < dim; d++) com[d]! /= Math.max(1, n);
    }
    let acc = 0;
    for (let i = 0; i < n; i += stride) {
      const b = i * dim;
      let r2 = 0;
      for (let d = 0; d < dim; d++) {
        const delta = view.positions[b + d]! - (mode === "origin" ? 0 : com[d]!);
        r2 += delta * delta;
      }
      acc += Math.sqrt(r2);
    }
    meanWellDist = acc / sc;
  }

  // —— Topology ——
  const epoch = view.topologyEpoch;
  const epochChanged = epoch !== prev.epoch;
  let nCycles = prev.graph?.nCycles ?? 0;
  let meanChainDepth = prev.graph?.meanChainDepth ?? 0;
  let meanFriendDist = prev.link?.meanFriendDist ?? 0;
  let meanEnemyDist = prev.link?.meanEnemyDist ?? 0;

  const wantTopo = audio.topologyVoice || audio.enabled;
  const refreshGraph =
    wantTopo &&
    (epochChanged ||
      prev.graph == null ||
      frameStamp - (prev.graph.frameStamp ?? 0) >= 8);
  if (refreshGraph && n > 0) {
    const { cycles } = functionalGraphCycles(view.friends);
    nCycles = cycles.length;
    const depths = chainDepths(view.friends);
    let ds = 0;
    for (let i = 0; i < depths.length; i++) ds += depths[i]!;
    meanChainDepth = depths.length ? ds / depths.length : 0;
  }

  const refreshLink =
    wantTopo &&
    (epochChanged ||
      prev.link == null ||
      frameStamp - (prev.link.frameStamp ?? 0) >= 8);
  if (refreshLink && n > 0) {
    const ld = meanLinkDistances(
      view.positions,
      dim,
      view.friends,
      view.enemies,
    );
    meanFriendDist = ld.meanFriend;
    meanEnemyDist = ld.meanEnemy;
  }

  const linkTension = linkTensionFromDists(meanFriendDist, meanEnemyDist);

  const frame: AudioFeatureFrame = {
    dtSec,
    n,
    dim,
    stride,
    kinematics: {
      meanSpeed,
      p90Speed,
      rmsSpeed,
      meanTurn,
      activeFrac,
    },
    gravity: {
      modeActive,
      mode,
      strengthNorm,
      meanWellDist,
      clump,
    },
    topology: {
      epoch,
      epochChanged,
      freeze: sim.freezeTopology,
      rechooseProb: sim.rechooseProb,
      nCycles,
      meanChainDepth,
      meanFriendDist,
      meanEnemyDist,
      linkTension,
    },
    social: {
      meanSocialStep: Math.hypot(sim.friendStep, sim.enemyStep),
    },
  };

  const next: FeatureBusPrev = {
    epoch,
    headings,
    graph: { nCycles, meanChainDepth, frameStamp },
    link: { meanFriendDist, meanEnemyDist, frameStamp },
  };

  return { frame, next };
}
